/**
 * Звонки этой вкладки (изменение v1.14, CLIENT_WEB §rtc).
 *
 * - Одна сессия на вкладку. Звонок устройства ведёт ровно одна вкладка: она держит Web Lock
 *   `dvizh-call`, остальные вкладки видят звонок в реплике и показывают «идёт в другой вкладке».
 * - Звонок, закреплённый за устройством, но без владельца замка (вкладку перезагрузили или
 *   закрыли), — сирота: ключи и соединение потеряны вместе с памятью вкладки, продолжить его
 *   нельзя, поэтому он завершается сразу, не дожидаясь срока пульса на сервере.
 * - Закрытие вкладки завершает звонок запросом `keepalive`.
 * - Гудки, звук завершения и удержание экрана (`Screen Wake Lock`) — по фазам сессии.
 */
import type { Call } from '@shared/api/types'
import { callsRepo } from '@shared/db'
import { microphoneMessage, openMicrophone, type MicrophoneFailure } from '@shared/rtc/microphone'
import { checkCallSupport, callUnsupportedMessage } from '@shared/rtc/support'
import { playTone, unlockTones, type ToneKind } from '@shared/rtc/tones'

import { callsApi, endCallOnUnload } from './api'
import type * as SessionModule from './session'
import type { CallDeps, CallFailure, CallSession, CallView, OutgoingCall } from './session'
import { useCallStore, type CallNotice } from './store'

const LOCK_NAME = 'dvizh-call'
/** Сколько экран звонка показывает итог, прежде чем закрыться. */
const SUMMARY_MS = 1_800

type WakeLockSentinelLike = { release: () => Promise<void> }

function failureNotice(failure: CallFailure): CallNotice {
  const microphone: Record<string, MicrophoneFailure> = {
    microphone_denied: 'denied',
    microphone_missing: 'not_found',
    microphone_busy: 'busy',
    microphone_failed: 'failed',
  }
  const reason = microphone[failure]
  if (reason) return { title: 'Микрофон недоступен', text: microphoneMessage(reason) }
  switch (failure) {
    case 'not_allowed':
      return { title: 'Звонок невозможен', text: 'Пользователь не принимает звонки от вас.' }
    case 'in_progress':
      return { title: 'Уже идёт звонок', text: 'Сначала завершите текущий звонок.' }
    case 'rate_limited':
      return { title: 'Слишком много звонков', text: 'Попробуйте позвонить чуть позже.' }
    case 'unavailable':
      return { title: 'Звонок недоступен', text: 'Звонок уже завершён или принят на другом устройстве.' }
    case 'security':
      return {
        title: 'Звонок прерван',
        text: 'Ключи шифрования не сошлись — соединение могли подменить. Звонок завершён ради вашей безопасности.',
      }
    case 'crypto':
      return { title: 'Звонки недоступны', text: callUnsupportedMessage('no_crypto') }
    case 'load':
      return {
        title: 'Не удалось загрузить звонки',
        text: 'Проверьте соединение с интернетом и обновите страницу.',
      }
    case 'relay_unavailable':
      return {
        title: 'Сервер-посредник недоступен',
        text: 'Звонок со скрытым IP-адресом сейчас невозможен. Разрешите прямое соединение в настройках звонков.',
      }
    default:
      return { title: 'Не удалось позвонить', text: 'Проверьте соединение с интернетом и попробуйте снова.' }
  }
}

class CallManager {
  private session: CallSession | null = null
  private unsubscribe: (() => void) | null = null
  private releaseLock: (() => void) | null = null
  private stopTone: (() => void) | null = null
  private tone: ToneKind | null = null
  private wakeLock: WakeLockSentinelLike | null = null
  private closeTimer: ReturnType<typeof setTimeout> | null = null
  private listenersInstalled = false
  /** Запуск идёт (проверки, микрофон, замок): второй «Позвонить» или «Ответить» ждать не будет. */
  private starting = false
  private readonly deps: CallDeps = {
    api: callsApi,
    openMicrophone,
    createPeer: (config) => new RTCPeerConnection(config),
    storeCall: (call) => callsRepo.put(call),
  }

  /** Есть ли у этой вкладки живая сессия звонка. */
  busy(): boolean {
    return this.session !== null && this.session.snapshot().phase !== 'ended'
  }

  async startOutgoing(call: OutgoingCall): Promise<boolean> {
    unlockTones()
    if (!this.beginStart()) return false
    try {
      // Итог прошлого звонка ещё на экране — убираем его и освобождаем его замок.
      if (!this.busy()) this.detach()
      const ready = await this.prepare()
      if (!ready) return false
      const session = await this.loadSession()
      if (!session) return false
      this.attach(session.CallSession.outgoing(this.deps, call))
      return true
    } finally {
      this.starting = false
    }
  }

  /** `ringing` — снимок входящего, увиденный до ответа: его commitment — эталон сверки ключей. */
  async accept(ringing: Call, relayOnly: boolean): Promise<boolean> {
    unlockTones()
    if (!this.beginStart()) return false
    try {
      if (!this.busy()) this.detach()
      useCallStore.getState().setAccepting(ringing.id)
      const ready = await this.prepare()
      const session = ready ? await this.loadSession() : null
      if (!session) {
        useCallStore.getState().setAccepting(null)
        return false
      }
      this.attach(session.CallSession.incoming(this.deps, ringing, relayOnly))
      return true
    } finally {
      this.starting = false
    }
  }

  async decline(call: Call): Promise<void> {
    try {
      await callsRepo.put(await callsApi.decline(call.id))
    } catch {
      /* звонок уже завершён — реплика догонит событием */
    }
  }

  hangup(): void {
    this.session?.hangup()
  }

  setMuted(muted: boolean): void {
    this.session?.setMuted(muted)
  }

  setOutput(deviceId: string): Promise<boolean> {
    return this.session?.setOutput(deviceId) ?? Promise.resolve(false)
  }

  /**
   * Умеет ли браузер звонить — до любых вопросов пользователю: спрашивать о прямом соединении,
   * чтобы следом сказать «звонки недоступны», незачем. Без поддержки — объяснение.
   */
  async ensureSupported(): Promise<boolean> {
    let support
    try {
      support = await checkCallSupport()
    } catch {
      // Модуль шифрования звонка не загрузился (сеть пропала) — звонить сейчас не выйдет.
      useCallStore.getState().setNotice(failureNotice('load'))
      return false
    }
    if (support.ok) return true
    useCallStore
      .getState()
      .setNotice({ title: 'Звонки недоступны', text: callUnsupportedMessage(support.reason) })
    return false
  }

  /** Выход из аккаунта или смена владельца: звонок завершается немедленно. */
  shutdown(): void {
    this.session?.hangup()
    this.detach()
  }

  /**
   * Сирота — незавершённый звонок этого устройства, которого не ведёт ни одна вкладка.
   * Без Web Locks проверить владельца нельзя — такой звонок завершит сервер по сроку пульса.
   */
  async endOrphan(call: Call): Promise<void> {
    if (this.busy() || this.session?.snapshot().callId === call.id) return
    const locks = globalThis.navigator?.locks
    if (!locks?.query) return
    const state = await locks.query()
    if (state.held?.some((lock) => lock.name === LOCK_NAME)) return
    try {
      await callsRepo.put(await callsApi.end(call.id, true))
    } catch {
      /* сервер завершит сам */
    }
  }

  /* --- внутреннее -------------------------------------------------------- */

  /**
   * Сессия звонка (WebRTC, криптография) — отдельный чанк, грузится на первом звонке. Не
   * загрузилась (сеть пропала) — объяснение и освобождение замка, взятого в prepare.
   */
  private async loadSession(): Promise<typeof SessionModule | null> {
    try {
      return await import('./session')
    } catch {
      this.detach()
      useCallStore.getState().setNotice(failureNotice('load'))
      return null
    }
  }

  /** Синхронная защита от двойного запуска: проверки дальше асинхронные и её не заменят. */
  private beginStart(): boolean {
    if (this.starting) return false
    this.starting = true
    return true
  }

  private async prepare(): Promise<boolean> {
    const store = useCallStore.getState()
    if (this.busy()) {
      store.setNotice({ title: 'Уже идёт звонок', text: 'Сначала завершите текущий звонок.' })
      return false
    }
    if (!(await this.ensureSupported())) return false
    // Замок прошлого звонка этой же вкладки освобождается асинхронно — одна повторная попытка.
    let locked = await this.acquireLock()
    if (!locked) {
      await new Promise((resolve) => setTimeout(resolve, 150))
      locked = await this.acquireLock()
    }
    if (!locked) {
      store.setNotice({ title: 'Уже идёт звонок', text: 'Звонок идёт в другой вкладке этого браузера.' })
      return false
    }
    this.installListeners()
    return true
  }

  /** Web Lock держится, пока жива сессия; закрытие вкладки освобождает его само. */
  private acquireLock(): Promise<boolean> {
    if (this.releaseLock) return Promise.resolve(true)
    const locks = globalThis.navigator?.locks
    if (!locks?.request) return Promise.resolve(true)
    return new Promise<boolean>((resolve) => {
      void locks
        .request(LOCK_NAME, { ifAvailable: true }, (lock) => {
          if (!lock) {
            resolve(false)
            return undefined
          }
          return new Promise<void>((release) => {
            this.releaseLock = release
            resolve(true)
          })
        })
        .catch(() => resolve(false))
    })
  }

  private attach(session: CallSession): void {
    // Прежняя живая сессия не остаётся без управления: микрофон, соединение и опрос гаснут.
    if (this.session && this.session !== session) {
      this.unsubscribe?.()
      this.session.hangup()
    }
    this.session = session
    this.unsubscribe = session.subscribe((view) => this.onView(view))
  }

  private detach(): void {
    if (this.closeTimer) clearTimeout(this.closeTimer)
    this.closeTimer = null
    this.unsubscribe?.()
    this.unsubscribe = null
    this.session = null
    this.setTone(null)
    void this.setWakeLock(false)
    this.releaseLock?.()
    this.releaseLock = null
    const store = useCallStore.getState()
    store.setView(null)
    store.setAccepting(null)
  }

  private onView(view: CallView): void {
    const store = useCallStore.getState()
    store.setView(view)
    if (view.phase !== 'starting') store.setAccepting(null)
    switch (view.phase) {
      case 'ringing':
        this.setTone('ringback')
        break
      case 'ended':
        if (view.failure) {
          this.setTone(null)
          store.setNotice(failureNotice(view.failure))
        } else if (view.endReason === 'busy' || view.endReason === 'declined') this.setTone('busy')
        else this.setTone('ended')
        void this.setWakeLock(false)
        if (!this.closeTimer) {
          const session = this.session
          this.closeTimer = setTimeout(
            () => {
              this.closeTimer = null
              if (this.session === session) this.detach()
            },
            view.failure ? 0 : SUMMARY_MS,
          )
        }
        break
      default:
        this.setTone(null)
        void this.setWakeLock(view.phase === 'active' || view.phase === 'reconnecting')
    }
  }

  private setTone(kind: ToneKind | null): void {
    if (this.tone === kind) return
    this.stopTone?.()
    this.stopTone = kind ? playTone(kind) : null
    this.tone = kind
  }

  /** Экран не гаснет, пока идёт разговор: на телефоне блокировка экрана обрывает звонок в браузере. */
  private async setWakeLock(on: boolean): Promise<void> {
    if (!on) {
      const lock = this.wakeLock
      this.wakeLock = null
      await lock?.release().catch(() => undefined)
      return
    }
    if (this.wakeLock || typeof document === 'undefined' || document.visibilityState !== 'visible') return
    const api = (
      globalThis.navigator as { wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> } }
    ).wakeLock
    if (!api) return
    try {
      this.wakeLock = await api.request('screen')
    } catch {
      this.wakeLock = null
    }
  }

  private installListeners(): void {
    if (this.listenersInstalled || typeof window === 'undefined') return
    this.listenersInstalled = true
    // bfcache может вернуть страницу, но соединение и ключи туда не вернутся — звонок завершается.
    window.addEventListener('pagehide', () => {
      const view = this.session?.snapshot()
      if (view && view.phase !== 'ended' && view.callId) {
        endCallOnUnload(view.callId)
        this.session?.hangup(true)
      }
    })
    document.addEventListener('visibilitychange', () => {
      const phase = this.session?.snapshot().phase
      // Разблокировка экрана снимает wake lock браузером — возвращаем его.
      if (document.visibilityState === 'visible' && (phase === 'active' || phase === 'reconnecting')) {
        this.wakeLock = null
        void this.setWakeLock(true)
      }
    })
  }
}

export const callManager = new CallManager()
